import { useState, useEffect, useCallback, RefObject } from 'react';
import { EditModePreviewHandle } from '../components/editor/EditModePreview';

// Background layer interface
export interface BackgroundLayer {
  id: string;
  type: 'linear-gradient' | 'radial-gradient' | 'image';
  value: string;
}

// Element data interface
export interface ElementData {
  editId: string | null;
  tagName: string;
  compName?: string | null;
  transform: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotation: number;
  };
  text?: {
    content: string;
    fontSize: number;
    fontFamily: string;
    fontWeight: string;
    color: string;
    textAlign: string;
  };
  background?: {
    backgroundColor: string;
    backgroundImage: string;
    backgroundSize: string;
    backgroundPosition: string;
    backgroundRepeat: string;
  };
  border?: {
    borderWidth: number;
    borderColor: string;
    borderStyle: string;
    borderRadius: number;
  };
  effects?: {
    opacity: number;
  };
  spacing?: {
    paddingTop: number;
    paddingRight: number;
    paddingBottom: number;
    paddingLeft: number;
    marginTop: number;
    marginRight: number;
    marginBottom: number;
    marginLeft: number;
  };
}

/**
 * Helper function to parse CSS background-image into layers
 */
function parseBackgroundImageToLayers(backgroundImage: string): BackgroundLayer[] {
  if (!backgroundImage || backgroundImage === 'none') {
    return [];
  }

  const layers: BackgroundLayer[] = [];
  
  // Split by commas that are not inside parentheses
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  
  for (let i = 0; i < backgroundImage.length; i++) {
    const char = backgroundImage[i];
    if (char === '(') depth++;
    else if (char === ')') depth--;
    else if (char === ',' && depth === 0) {
      parts.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  if (current.trim()) parts.push(current.trim());

  // Parse each part
  parts.forEach((part, index) => {
    const trimmed = part.trim();
    if (trimmed.startsWith('linear-gradient')) {
      layers.push({
        id: `layer-${Date.now()}-${index}`,
        type: 'linear-gradient',
        value: trimmed
      });
    } else if (trimmed.startsWith('radial-gradient')) {
      layers.push({
        id: `layer-${Date.now()}-${index}`,
        type: 'radial-gradient',
        value: trimmed
      });
    } else if (trimmed.startsWith('url(')) {
      layers.push({
        id: `layer-${Date.now()}-${index}`,
        type: 'image',
        value: trimmed
      });
    }
  });

  return layers;
}

export interface Section {
  name: string;
  editId: string;
  isActive?: boolean;
}

export interface Template {
  name: string;
  editId: string;
}

/**
 * Hook to manage visual editor state and element property updates
 * Consolidates 6 duplicate property handlers into 1 generic updater
 */
export function useVisualEditor(
  editModePreviewRef: RefObject<EditModePreviewHandle>,
  onElementSelected?: (data: { editId: string; label: string } | null) => void
) {
  const [selectedElementData, setSelectedElementData] = useState<ElementData | null>(null);
  const [lockSize, setLockSize] = useState(true);
  const [sections, setSections] = useState<Section[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);

  /**
   * Generic property updater - replaces 6 duplicate functions!
   * Handles all property updates for all categories
   */
  const updateElementProperty = useCallback((
    category: 'transform' | 'text' | 'background' | 'border' | 'effects' | 'spacing',
    property: string,
    value: any,
    messageType?: string
  ) => {
    if (!selectedElementData?.editId) return;

    // Update local state
    setSelectedElementData(prev => {
      if (!prev || !prev[category]) return prev;
      return {
        ...prev,
        [category]: { ...prev[category], [property]: value }
      };
    });

    // Send to iframe
    const iframe = editModePreviewRef.current;
    if (iframe?.iframeRef?.current?.contentWindow) {
      const type = messageType || `update-${category}-style`;
      
      iframe.iframeRef.current.contentWindow.postMessage({
        type,
        editId: selectedElementData.editId,
        property,
        value
      }, '*');
    }
  }, [selectedElementData, editModePreviewRef]);

  // Transform property changes
  const handleTransformChange = useCallback((
    property: 'x' | 'y' | 'scaleX' | 'scaleY' | 'rotation',
    value: number
  ) => {
    updateElementProperty('transform', property, value, 'update-transform');
  }, [updateElementProperty]);

  // Text property changes
  const handleTextChange = useCallback((
    property: 'content' | 'fontSize' | 'fontFamily' | 'fontWeight' | 'color' | 'textAlign',
    value: string | number
  ) => {
    if (!selectedElementData?.editId || !editModePreviewRef.current) return;

    // Update local state
    setSelectedElementData(prev => {
      if (!prev || !prev.text) return prev;
      return {
        ...prev,
        text: {
          ...prev.text,
          [property]: value
        }
      };
    });

    // Send message to iframe
    const iframe = editModePreviewRef.current;
    if (iframe?.iframeRef?.current?.contentWindow) {
      const messageType = property === 'content' ? 'update-text-content' : 'update-text-style';
      iframe.iframeRef.current.contentWindow.postMessage({
        type: messageType,
        editId: selectedElementData.editId,
        property,
        value
      }, '*');
    }
  }, [selectedElementData, editModePreviewRef]);

  // Background property changes
  const handleBackgroundChange = useCallback((
    property: 'backgroundColor' | 'backgroundImage' | 'backgroundSize' | 'backgroundPosition' | 'backgroundRepeat' | 'layers',
    value: string | BackgroundLayer[]
  ) => {
    if (!selectedElementData?.editId || !editModePreviewRef.current) return;

    // Handle layers specially
    if (property === 'layers') {
      const layers = value as BackgroundLayer[];
      
      // Build the CSS background-image string from layers
      const backgroundImageValue = layers
        .map(layer => layer.value)
        .filter(v => v)
        .join(', ') || 'none';

      // Update local state with both layers and backgroundImage
      setSelectedElementData(prev => {
        if (!prev || !prev.background) return prev;
        return {
          ...prev,
          background: {
            ...prev.background,
            layers,
            backgroundImage: backgroundImageValue
          }
        };
      });

      // Send backgroundImage to iframe
      const iframe = editModePreviewRef.current;
      if (iframe?.iframeRef?.current?.contentWindow) {
        iframe.iframeRef.current.contentWindow.postMessage({
          type: 'update-background-style',
          editId: selectedElementData.editId,
          property: 'backgroundImage',
          value: backgroundImageValue
        }, '*');
      }
      return;
    }

    // Handle regular properties
    updateElementProperty('background', property, value as string, 'update-background-style');
  }, [selectedElementData, editModePreviewRef, updateElementProperty]);

  // Border property changes
  const handleBorderChange = useCallback((
    property: 'borderWidth' | 'borderColor' | 'borderStyle' | 'borderRadius',
    value: string | number
  ) => {
    updateElementProperty('border', property, value, 'update-border-style');
  }, [updateElementProperty]);

  // Effects property changes
  const handleEffectsChange = useCallback((
    property: 'opacity',
    value: number
  ) => {
    updateElementProperty('effects', property, value, 'update-effects-style');
  }, [updateElementProperty]);

  // Spacing property changes
  const handleSpacingChange = useCallback((
    property: 'paddingTop' | 'paddingRight' | 'paddingBottom' | 'paddingLeft' | 'marginTop' | 'marginRight' | 'marginBottom' | 'marginLeft',
    value: number
  ) => {
    updateElementProperty('spacing', property, value, 'update-spacing-style');
  }, [updateElementProperty]);

  // Lock size toggle
  const handleLockSizeChange = useCallback((locked: boolean) => {
    setLockSize(locked);

    const iframe = editModePreviewRef.current;
    if (iframe?.iframeRef?.current?.contentWindow) {
      iframe.iframeRef.current.contentWindow.postMessage({
        type: 'set-lock-size',
        locked
      }, '*');
    }
  }, [editModePreviewRef]);

  // Extract sections and templates from iframe
  const extractSectionsAndTemplates = useCallback(() => {
    const iframe = editModePreviewRef.current?.iframeRef.current;
    if (!iframe?.contentWindow?.document) return;

    const doc = iframe.contentWindow.document;
    
    // Extract sections and detect which one is visible/active
    const sectionElements = doc.querySelectorAll('section[section-name]');
    const extractedSections: Section[] = [];
    sectionElements.forEach((section) => {
      const name = section.getAttribute('section-name');
      const editId = section.getAttribute('data-edit-id');
      if (name && editId) {
        const htmlElement = section as HTMLElement;
        const computedStyle = iframe.contentWindow!.getComputedStyle(htmlElement);
        
        // Check if section is visible (display not 'none' and visibility not 'hidden')
        const isVisible = computedStyle.display !== 'none' && 
                         computedStyle.visibility !== 'hidden' &&
                         htmlElement.offsetParent !== null;
        
        extractedSections.push({ 
          name, 
          editId,
          isActive: isVisible
        });
      }
    });

    // Extract templates
    const templateElements = doc.querySelectorAll('template[template-name]');
    const extractedTemplates: Template[] = [];
    templateElements.forEach((template) => {
      const name = template.getAttribute('template-name');
      const editId = template.getAttribute('data-edit-id');
      if (name && editId) {
        extractedTemplates.push({ name, editId });
      }
    });

    setSections(extractedSections);
    setTemplates(extractedTemplates);
    
    console.log('Extracted from iframe:', {
      sections: extractedSections,
      templates: extractedTemplates,
      activeSection: extractedSections.find(s => s.isActive)?.name
    });
  }, [editModePreviewRef]);

  // Listen for edit mode ready and extract sections/templates
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'edit-mode-ready') {
        // Extract sections and templates when edit mode is ready
        setTimeout(() => extractSectionsAndTemplates(), 100);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [extractSectionsAndTemplates]);

  // Listen for sections-updated message to re-extract active section
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'sections-updated') {
        // Re-extract sections to update active state
        setTimeout(() => extractSectionsAndTemplates(), 50);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [extractSectionsAndTemplates]);

  // Listen for selection changes from edit mode iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'selection-changed') {
        if (event.data.editId) {
          // Parse transform values from the selection data
          const transform = {
            x: event.data.transform?.x || 0,
            y: event.data.transform?.y || 0,
            scaleX: event.data.transform?.scaleX || 1,
            scaleY: event.data.transform?.scaleY || 1,
            rotation: event.data.transform?.rotation || 0
          };
          
          // Parse text data if available
          const text = event.data.text ? {
            content: event.data.text.content || '',
            fontSize: event.data.text.fontSize || 16,
            fontFamily: event.data.text.fontFamily || 'Arial, sans-serif',
            fontWeight: event.data.text.fontWeight || '400',
            color: event.data.text.color || '#000000',
            textAlign: event.data.text.textAlign || 'left'
          } : undefined;
          
          // Parse background data if available
          const background = event.data.background ? {
            backgroundColor: event.data.background.backgroundColor || 'transparent',
            backgroundImage: event.data.background.backgroundImage || 'none',
            backgroundSize: event.data.background.backgroundSize || 'auto',
            backgroundPosition: event.data.background.backgroundPosition || 'center',
            backgroundRepeat: event.data.background.backgroundRepeat || 'no-repeat',
            layers: parseBackgroundImageToLayers(event.data.background.backgroundImage || 'none')
          } : undefined;
          
          // Parse border data if available
          const border = event.data.border ? {
            borderWidth: event.data.border.borderWidth || 0,
            borderColor: event.data.border.borderColor || '#000000',
            borderStyle: event.data.border.borderStyle || 'solid',
            borderRadius: event.data.border.borderRadius || 0
          } : undefined;
          
          // Parse effects data if available
          const effects = event.data.effects ? {
            opacity: event.data.effects.opacity || 1
          } : undefined;
          
          // Parse spacing data if available
          const spacing = event.data.spacing ? {
            paddingTop: event.data.spacing.paddingTop || 0,
            paddingRight: event.data.spacing.paddingRight || 0,
            paddingBottom: event.data.spacing.paddingBottom || 0,
            paddingLeft: event.data.spacing.paddingLeft || 0,
            marginTop: event.data.spacing.marginTop || 0,
            marginRight: event.data.spacing.marginRight || 0,
            marginBottom: event.data.spacing.marginBottom || 0,
            marginLeft: event.data.spacing.marginLeft || 0
          } : undefined;
          
          setSelectedElementData({
            editId: event.data.editId,
            tagName: event.data.tagName || 'Unknown',
            compName: event.data.compName || null,
            transform,
            text,
            background,
            border,
            effects,
            spacing
          });

          // Notify callback with element selection event
          if (onElementSelected) {
            try {
              const label = event.data.compName || event.data.tagName || 'element';
              onElementSelected({
                editId: event.data.editId,
                label,
              });
            } catch (error) {
              console.error('[useVisualEditor] Error in onElementSelected callback:', error);
            }
          }
        } else {
          // No element selected
          setSelectedElementData(null);
          
          // Notify callback with null selection
          if (onElementSelected) {
            try {
              onElementSelected(null);
            } catch (error) {
              console.error('[useVisualEditor] Error in onElementSelected callback (deselect):', error);
            }
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onElementSelected]);

  return {
    selectedElementData,
    lockSize,
    setLockSize: handleLockSizeChange,
    handleTransformChange,
    handleTextChange,
    handleBackgroundChange,
    handleBorderChange,
    handleEffectsChange,
    handleSpacingChange,
    sections,
    templates
  };
}

